"use client";

import { useCallback } from "react";
import { useWalletActionGuard } from "./useWalletActionGuard";
import { useSendTransaction } from "wagmi";

type SendTransactionParams = Parameters<ReturnType<typeof useSendTransaction>["sendTransactionAsync"]>[0];

/**
 * Wrapper hook for wagmi's useSendTransaction that ensures the wallet is connected before sending.
 *
 * Scenario: User is authenticated with Privy but their wallet got disconnected
 * (e.g., MetaMask disconnected, mobile in-app browser session dropped after phone sleep).
 * This hook performs a single guarded send attempt: it reconnects the user's previous wallet
 * connector before sending the transaction, but does not retry on failure.
 *
 * Uses wagmi's useReconnect (connector-agnostic) instead of hardcoding injected(),
 * so it works with MetaMask, WalletConnect, Rabby, and any other connector type.
 *
 * @returns {Object} Hook return value
 * @returns {Function} sendTransactionAsync - Sends a transaction after validating the authenticated wallet.
 * @returns {boolean} isPending - Whether a transaction is in progress
 */
export const useSendTransactionWithConnection = () => {
  const { executeMainEvmAction } = useWalletActionGuard();
  const { sendTransactionAsync: wagmiSendTransactionAsync, isPending } = useSendTransaction();

  const sendTransactionAsync = useCallback(
    async (params: SendTransactionParams): Promise<`0x${string}`> => {
      // Never auto-retry a transaction send. Some mobile/WalletConnect flows can surface a connector
      // error after the wallet has already received the request, and retrying here risks duplicate prompts.
      return executeMainEvmAction(() => wagmiSendTransactionAsync(params));
    },
    [executeMainEvmAction, wagmiSendTransactionAsync],
  );

  return {
    sendTransactionAsync,
    isPending,
  };
};
